import { WSCard, WSList, WSText } from "@wingspanhq/fe-component-library";
import React from "react";
import { IFormNote, IFormSchemaSection } from "../types";
import { selectorVisibleFields } from "../selectors/selectorVisibleFields";
import { isHiddenUiField } from "../utils/fieldRules";
import { isPrefilledReadOnlyField } from "../utils/isSharedPiiField";
import { DynamicFormFieldRenderer } from "./DynamicFormFieldRenderer";
import { DynamicFormNote } from "./DynamicFormNote";

type Props = {
  section: IFormSchemaSection;
};

export const DynamicFormSection: React.FC<Props> = ({ section }) => {
  const visibleFields = selectorVisibleFields(section.fields);
  // Hidden-UI fields (worksheet intermediates) render nothing but must ALWAYS
  // be mounted so their values stay RHF-registered — even when the section's
  // card itself is empty and skipped.
  const hiddenFields = visibleFields.filter(isHiddenUiField);
  // Pre-filled (read-only) fields are surfaced separately in the "Pre-filled
  // data" card, so the section renders only the contractor-editable fields.
  const editableFields = visibleFields.filter(
    field => !isPrefilledReadOnlyField(field) && !isHiddenUiField(field)
  );
  // Warnings first, then info notes — matches the form- and field-level ordering.
  const sectionNotes: IFormNote[] = [...section.warnings, ...section.notes];

  const hiddenElements = hiddenFields.map(field => (
    <DynamicFormFieldRenderer key={field.key} field={field} />
  ));

  // A description-only card (e.g. a worksheet intro) still renders — skip the
  // card only when there is nothing at all to show.
  if (
    editableFields.length === 0 &&
    sectionNotes.length === 0 &&
    !section.description
  ) {
    return <>{hiddenElements}</>;
  }

  return (
    <>
      {hiddenElements}
      <WSCard
        header={section.title ? { label: { text: section.title } } : undefined}
        divider={section.title ? "bottom" : false}
      >
        {/* "XL" between fields so each input's label/note cluster reads as one
            unit (a field's own notes stay tight at gap "S" inside the field
            renderer) — at "L" a checkbox and the next field's description still
            visually ran together (Info-Architecture Figma 1577-51543 rhythm). */}
        <WSList gap="XL">
          {section.description ? (
            <WSText.ParagraphSm color="gray500">
              {section.description}
            </WSText.ParagraphSm>
          ) : null}
          {sectionNotes.map((note, i) => (
            <DynamicFormNote key={`${section.key}-note-${i}`} note={note} />
          ))}
          {editableFields.map(field => (
            <DynamicFormFieldRenderer key={field.key} field={field} />
          ))}
        </WSList>
      </WSCard>
    </>
  );
};
